"use client";

import Image from "next/image";
import Link from "next/link";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import { useRef } from "react";
import BlurFadeText from "@/components/common/blurFadeText";
import FullContactForm from "@/components/forms/fullContactForm";
import SectionEyebrow from "@/components/common/sectionEyebrow";
import SectionTitle from "@/components/common/sectionTitle";
import PlatformOverviewExperience from "@/components/sections/platform/platformOverviewExperience";
import FAIcon from "@/components/icons/faIcon";
import {
  PlatformCapabilityGridSection,
  SlidingShowcaseCard,
} from "@/components/sections/platform/platformStorySections";
import Button from "@/components/common/button";
import PlatformFAQSection from "@/components/sections/platform/platformFaqSection";
import {
  VariantB_FinishedProduct,
  VariantB_FieldWorkflows,
} from "@/components/sections/platform/platformNewSections";

const EASE_OUT = [0.22, 1, 0.36, 1];

const FEATURE_AREAS = [
  {
    eyebrow: "Audits and risk",
    title: "Audit, assess and prioritise with more clarity",
    description:
      "Run audits and risk assessments with structured scoring, executive summaries, hazard tracking and version history so teams can see where attention is needed first.",
    image: "/images/other/sml-platform-laptop.webp",
    alt: "SML Connect audit and risk features shown on screen",
  },
  {
    eyebrow: "Checks and tasks",
    title: "Keep site checks and recurring tasks moving",
    description:
      "Assign scheduled checklists to the right sites, roles and locations, then monitor completion through a clear activity trail.",
  },
  {
    eyebrow: "Incidents",
    title: "Report incidents faster and with better detail",
    description:
      "Capture incidents through configurable workflows with question sets, media uploads, RIDDOR-related detail and lost time tracking where required.",
  },
  {
    eyebrow: "Documents",
    title: "Control documents without losing context",
    description:
      "Store policies, records and supporting files in a central library linked back to sites, departments and related safety activity.",
  },
  {
    eyebrow: "Multi-site management",
    title: "Support multi-site customers without adding complexity",
    description:
      "Manage accounts, sites, departments, contacts and portal users in one place, with permissions and structure that scale across larger organisations.",
  },
];

const PLATFORM_EXPERIENCE_CARDS = [
  {
    icon: "building",
    title: "Site-wide visibility",
    description:
      "See audits, incidents and overdue actions across sites without piecing reports together manually.",
    delay: 0.04,
    axis: "-x",
    className: "lg:col-span-4 lg:col-start-2 lg:row-start-1",
  },
  {
    icon: "clock",
    title: "Faster follow-up",
    description:
      "Turn findings into owned actions with due dates, status changes and a visible trail.",
    delay: 0.17,
    axis: "x",
    className: "lg:col-span-4 lg:col-start-7 lg:row-start-1",
  },
  {
    icon: "rotate",
    title: "Mobile ready",
    description:
      "Complete checks, log observations and upload evidence from the floor instead of after the fact.",
    delay: 0.1,
    axis: "-x",
    className: "lg:col-span-4 lg:col-start-1 lg:row-start-2",
  },
  {
    icon: "bolt",
    title: "Practical reporting",
    description:
      "Use dashboards and executive summaries to spot drift early and focus attention where it matters.",
    delay: 0.22,
    axis: "x",
    className: "lg:col-span-4 lg:col-start-9 lg:row-start-2",
  },
  {
    icon: "file-contract",
    title: "Evidence attached",
    description:
      "Keep photos, notes, documents and follow-up connected to the record that created the work.",
    delay: 0.14,
    axis: "-x",
    className: "lg:col-span-4 lg:col-start-1 lg:row-start-3",
  },
  {
    icon: "shield-halved",
    title: "Secure control",
    description:
      "Permissions and structured access keep information available to the right people without losing oversight.",
    delay: 0.25,
    axis: "x",
    className: "lg:col-span-4 lg:col-start-9 lg:row-start-3",
  },
];

const OPERATIONS_GROUPS = [
  {
    title: "Operational control",
    description:
      "Designed to help teams stay on top of the day-to-day work, not just produce records.",
    items: [
      "Central action centre",
      "Outstanding and review dashboards",
      "Role-based assignment",
      "Activity logs across key records",
    ],
  },
  {
    title: "Field-ready workflows",
    description:
      "Useful for live environments where checks, follow-up and evidence capture need to happen quickly.",
    items: [
      "QR code checks",
      "Photo, video and audio uploads",
      "Mobile and tablet responsive",
      "PWA support for iOS and Android",
    ],
  },
  {
    title: "Governance and control",
    description:
      "Strong enough for growing businesses that need consistency, accountability and access control.",
    items: [
      "Granular permissions",
      "Version history",
      "Secure document access",
      "Structured customer and site hierarchy",
    ],
  },
];

const OVERVIEW_POINTS = [
  "Multi-site customer and department structure",
  "Granular permissions across key modules",
  "Version history and activity logs",
  "Responsive access for desktop, tablet and mobile",
];

const HERO_METRICS = [
  {
    value: "100%",
    icon: "check",
    description:
      "Maintain complete traceability across audits, incidents, documents and actions.",
  },
  {
    value: "LIVE",
    icon: "wifi",
    description: "Monitor performance, actions and incidents in real time.",
  },
  {
    value: "24/7",
    icon: "clock",
    description: "Access records, evidence and operational data from anywhere.",
  },
];

const PLATFORM_FINISHED_PRODUCT_CARDS = [
  {
    title: "Configured workflows",
    description:
      "Set up audits, incidents and action handling around the way your teams already work, with forms, owners and follow-up already defined.",
    panel: "workflow",
  },
  {
    title: "Clear operator dashboard",
    description:
      "Give managers and site teams a direct view of outstanding work, recent activity and the core modules they need every day.",
    panel: "dashboard",
  },
  {
    title: "Structured user control",
    description:
      "Keep user access, permissions and account structure in place from the start so growth does not create reporting or governance gaps.",
    panel: "account",
  },
];

function revealProps(shouldReduceMotion, delay = 0, axis = "y", distance = 24) {
  if (shouldReduceMotion) {
    return {};
  }

  const hidden = { opacity: 0, filter: "blur(10px)" };

  if (axis === "x") {
    hidden.x = distance;
  } else if (axis === "-x") {
    hidden.x = -distance;
  } else {
    hidden.y = distance;
  }

  return {
    initial: hidden,
    whileInView: { opacity: 1, x: 0, y: 0, filter: "blur(0px)" },
    viewport: { once: true, amount: 0.22 },
    transition: { duration: 0.78, delay, ease: EASE_OUT },
  };
}

function HeroSnippetCard({
  src,
  alt,
  label,
  className = "",
  imageClassName = "object-cover object-center",
  aspectClassName = "aspect-square",
  delay = 0.12,
  axis = "y",
}) {
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.div
      {...revealProps(shouldReduceMotion, delay, axis, 18)}
      className={className}
    >
      <div className="relative overflow-hidden rounded-[1.4rem] border border-black/8 bg-white shadow-[0_18px_50px_rgba(6,4,12,0.10)]">
        <div className={`relative ${aspectClassName} w-full`}>
          <Image
            src={src}
            alt={alt}
            fill
            sizes="(max-width: 1536px) 18rem, 20rem"
            className={imageClassName}
          />
        </div>
      </div>
      <div className="mt-3 inline-flex rounded-full bg-white/92 px-4 py-2 text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-[#06040c]/58 shadow-[0_12px_34px_rgba(6,4,12,0.08)]">
        {label}
      </div>
    </motion.div>
  );
}

function ShowcaseCard({ item, index, tone = "light", className = "" }) {
  const shouldReduceMotion = useReducedMotion();
  const tones = {
    light:
      "border border-[#ece5d7] bg-[linear-gradient(180deg,#fffdf8_0%,#f7f2e7_100%)]",
    warm: "border border-[#eadfcb] bg-[linear-gradient(180deg,#f8f0df_0%,#f2e7d1_100%)]",
    dark: "bg-[#0d1011] text-white",
  };

  return (
    <motion.article
      {...revealProps(
        shouldReduceMotion,
        0.08 + index * 0.04,
        index % 2 === 0 ? "-x" : "x",
        18,
      )}
      className={`relative overflow-hidden rounded-[1.5rem] p-7  sm:p-8 ${className}`}
    >
      <div className="relative flex h-full flex-col">
        <p
          className={`text-xs font-semibold uppercase tracking-[0.22em] ${
            tone === "dark" ? "text-white/52" : "text-[#06040c]/42"
          }`}
        >
          {item.eyebrow}
        </p>
        <h3
          className={`mt-4 max-w-xs font-raleway text-[1.6rem] font-semibold leading-[0.96] tracking-tighter sm:text-[1.95rem] ${
            tone === "dark" ? "text-white" : "text-[#06040c]"
          }`}
        >
          {item.title}
        </h3>
        <p
          className={`mt-4 max-w-md text-base leading-7 sm:text-[1.02rem] sm:leading-8 ${
            tone === "dark" ? "text-white/70" : "text-[#06040c]/62"
          }`}
        >
          {item.description}
        </p>
      </div>
    </motion.article>
  );
}

function PlatformExperienceCard({ card }) {
  const shouldReduceMotion = useReducedMotion();
  const cardRevealStart = 0.03 + card.delay * 0.65;
  const cardRevealEnd = cardRevealStart + 0.35;
  const revealOpacity = useTransform(
    card.scrollYProgress,
    [cardRevealStart, cardRevealEnd],
    [0, 1],
  );
  const revealY = useTransform(
    card.scrollYProgress,
    [cardRevealStart, cardRevealEnd],
    [160, 0],
  );
  const revealBlur = useTransform(
    card.scrollYProgress,
    [cardRevealStart, cardRevealEnd],
    ["blur(10px)", "blur(0px)"],
  );

  return (
    <div
      className={`relative overflow-hidden rounded-[1.75rem] ${card.className}`}
    >
      <motion.article
        style={
          shouldReduceMotion
            ? undefined
            : {
                opacity: revealOpacity,
                y: revealY,
                filter: revealBlur,
              }
        }
        className="relative rounded-[1.75rem] bg-white/5 p-6 backdrop-blur-sm sm:p-7"
      >
        {}
        <div className="relative">
          <div className="flex gap-4 items-center">
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-primary/12 text-primary">
              <FAIcon icon={card.icon} noMargin className="h-3 w-3" />
            </div>
            <h3 className="font-raleway text-[1.5rem] font-semibold leading-[0.96] tracking-tighter text-white sm:text-[1.7rem]">
              {card.title}
            </h3>
          </div>
          <p className="mt-3 text-sm leading-6 text-white/68 sm:text-base sm:leading-7">
            {card.description}
          </p>
        </div>
      </motion.article>
    </div>
  );
}

function OperationsGroup({ group, index }) {
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.article
      {...revealProps(
        shouldReduceMotion,
        0.08 + index * 0.05,
        index % 2 === 0 ? "-x" : "x",
        18,
      )}
      className="rounded-lg bg-white/70 p-7 sm:p-8"
    >
      <h3 className="font-raleway text-[1.7rem] font-semibold leading-[0.96] tracking-tighter text-[#06040c] sm:text-[2rem]">
        {group.title}
      </h3>
      <p className="mt-4 max-w-lg text-base leading-7 text-[#06040c]/62 sm:text-[1.02rem] sm:leading-8">
        {group.description}
      </p>

      <div className="mt-8 flex flex-wrap gap-3">
        {group.items.map((item) => (
          <div
            key={item}
            className="flex items-center gap-3 rounded-md bg-neutral-50 px-5 py-3 text-sm leading-6 text-[#06040c]/74 sm:text-base"
          >
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
            <span>{item}</span>
          </div>
        ))}
      </div>
    </motion.article>
  );
}

function PlatformOperationsSection() {
  const shouldReduceMotion = useReducedMotion();

  return (
    <section className="mx-auto w-full max-w-328 space-y-16">
      <motion.div
        {...revealProps(shouldReduceMotion)}
        className="grid gap-6 lg:grid-cols-[minmax(0,0.76fr)_minmax(280px,0.64fr)] lg:items-end"
      >
        <div className="space-y-5 ">
          <SectionEyebrow
            text="Built for real use"
            className="pt-0"
            textClassName="text-black/52"
            amount={0.35}
          />
          <SectionTitle
            className="text-[2.6rem] sm:text-[3.2rem]"
            text="Useful in the field, structured enough for management and governance."
          />
        </div>
        <p className="max-w-2xl text-base leading-7 text-[#06040c]/62 sm:text-[1.02rem] sm:leading-8">
          <BlurFadeText
            text="The strongest platform story is not just what modules exist, but how well they support the pace, visibility and control teams need day to day."
            delay={0.16}
            blur={8}
            y={16}
          />
        </p>
      </motion.div>

      <div className="grid gap-8 lg:grid-cols-3">
        {OPERATIONS_GROUPS.map((group, index) => (
          <OperationsGroup key={group.title} group={group} index={index} />
        ))}
      </div>
    </section>
  );
}

function MobileWorkflowsSection() {
  const shouldReduceMotion = useReducedMotion();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const imageSlide = useTransform(scrollYProgress, [0, 0.5], [80, 0]);

  return (
    <section ref={sectionRef} className="relative w-full overflow-hidden -mb-4">
      {}
      <div className="absolute inset-0 bg-neutral-100 rounded-lg" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_60%_50%,rgba(254,201,80,0.05),transparent_70%)]" />

      <div className="relative z-10 mx-auto w-full max-w-328 px-4 sm:px-6 lg:px-8 ">
        <div className="grid items-center gap-10 lg:grid-cols-[1fr_1.3fr] lg:gap-0">
          {}
          <motion.div
            {...revealProps(shouldReduceMotion, 0.06, "y", 20)}
            className="order-1 lg:order-2"
          >
            {}
            <SectionTitle
              className="mt-5 max-w-2xl text-[2rem] sm:text-[3rem]"
              text="Carry out checks on site and keep the follow-up connected."
            />
            <p className="mt-4 sm:mt-5 max-w-xl text-sm sm:text-base leading-7 sm:leading-7 mb-6 sm:mb-8">
              <BlurFadeText
                text="SML Connect supports QR-based checks, touch-friendly workflows and media capture so site activity can be recorded and progressed without waiting to get back to a desk."
                delay={0.14}
                blur={8}
                y={16}
              />
            </p>
            <div className="flex flex-wrap gap-3 sm:gap-4">
              <a
                href="https://apps.apple.com/gb/app/sml-connect/id6473526886"
                target="_blank"
                rel="noreferrer"
                aria-label="Download SML Connect on the App Store"
              >
                <Image
                  src="/images/app/apple-store-button.svg"
                  alt="Download on the App Store"
                  width={180}
                  height={45}
                  className="mt-8 h-13 w-auto rounded-md object-contain"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=biz.crushedice.sml.connect"
                target="_blank"
                rel="noreferrer"
                aria-label="Get SML Connect on Google Play"
              >
                <Image
                  src="/images/app/google-play-button.svg"
                  alt="Get it on Google Play"
                  width={180}
                  height={45}
                  className="mt-8 h-13 w-auto rounded-md object-contain"
                />
              </a>
            </div>
          </motion.div>

          {}
          <motion.div
            style={shouldReduceMotion ? undefined : { y: imageSlide }}
            className="relative order-2 flex items-center justify-center lg:order-1 pt-10 sm:pt-16 lg:pt-20"
          >
            <div className="relative mx-auto w-full max-w-56 sm:max-w-64 lg:max-w-72 xl:max-w-120">
              <div className="absolute  h-24 rounded-full bg-primary/20 blur-[90px]" />
              <div className="relative aspect-30/40 w-full">
                <Image
                  src="/images/platform/phone-hand-connect-login.webp"
                  alt="Hand holding the SML Connect mobile interface"
                  fill
                  sizes="(max-width: 1024px) 60vw, 30vw"
                  className="object-contain object-center"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function PlatformOverviewStatsSection() {
  const shouldReduceMotion = useReducedMotion();

  return (
    <div className="w-full">
      <div className="px-4 bg-white rounded-lg py-18">
        <section className="mx-auto flex w-full max-w-328 flex-col items-center px-4 pb-2 pt-2 text-center sm:px-6 lg:px-8">
          <motion.div
            {...revealProps(shouldReduceMotion, 0.02, "y", 20)}
            className="flex flex-col space-y-8 max-w-5xl"
          >
            <h2 className="max-w-3xl font-raleway text-[2.6rem] sm:text-[4rem] font-semibold leading-[0.92] tracking-[-0.065em] text-black">
              <BlurFadeText
                text="Automation that actually understands your business."
                delay={0.08}
                amount={0.35}
                blur={11}
                y={20}
              />
            </h2>
            <p className="max-w-xl mx-auto font-raleway text-[0.95rem] sm:text-[1.1rem] leading-[1.35] text-[#7a807d]">
              <BlurFadeText
                text=" SML Connect brings audits, incidents, follow-up and document control
          into one operational system so teams can move faster without losing
          structure, oversight or traceability."
                delay={0.16}
                amount={0.35}
                blur={8}
                y={16}
              />
            </p>

            {}
          </motion.div>

          <div className="mt-16 grid w-full gap-10 text-left lg:grid-cols-3 lg:gap-12 xl:mt-4">
            {HERO_METRICS.map((metric, index) => (
              <motion.article
                key={metric.value}
                {...revealProps(
                  shouldReduceMotion,
                  0.16 + index * 0.06,
                  index === 1 ? "y" : index === 0 ? "-x" : "x",
                  20,
                )}
                className="flex flex-col items-center px-4 py-6 sm:px-4 sm:py-9"
              >
                <div className="p-4 rounded-lg border-[1.5px] w-14 h-14 mb-8 border-zinc-100">
                  <FAIcon icon={metric.icon} className="h-5 w-5" noMargin />
                </div>
                <div className="text-[3.2rem] sm:text-[4rem] text-center font-semibold leading-none tracking-tighter text-[#06040c]">
                  {metric.value}
                </div>
                <p className="mt-5 max-w-lg text-base text-center text-[#06040c] sm:text-[1.02rem]">
                  {metric.description}
                </p>
              </motion.article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function ProductPreviewPanel({ panel }) {
  if (panel === "workflow") {
    return (
      <div className="mx-auto flex h-full w-full max-w-72 flex-col rounded-[1.4rem] bg-white p-4 shadow-[0_16px_32px_rgba(6,4,12,0.06)] sm:max-w-76 sm:p-5">
        <div className="flex items-center justify-between text-[0.74rem] font-medium text-[#06040c]/62">
          <span>New workflow</span>
          <div className="flex items-center gap-2">
            <span className="h-7 w-7 rounded-lg border border-[#ece7dc]" />
            <span className="h-7 w-7 rounded-lg border border-[#ece7dc]" />
          </div>
        </div>
        <div className="mt-6 flex items-center gap-3">
          <span className="h-2.5 w-2.5 rounded-full bg-[#9fe2b5]" />
          <span className="text-[1.02rem] font-semibold text-[#06040c]">
            Incident follow-up
          </span>
        </div>
        <p className="mt-4 text-[0.78rem] leading-6 text-[#06040c]/58">
          Build routing, owners, deadlines and evidence steps into one usable
          process.
        </p>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <div className="rounded-[0.9rem] border border-[#ece7dc] px-3 py-2.5">
            <div className="text-[0.64rem] uppercase tracking-[0.16em] text-[#06040c]/36">
              SLA
            </div>
            <div className="mt-1 text-[0.82rem] font-medium text-[#06040c]/72">
              24 hour review
            </div>
          </div>
          <div className="rounded-[0.9rem] border border-[#ece7dc] px-3 py-2.5">
            <div className="text-[0.64rem] uppercase tracking-[0.16em] text-[#06040c]/36">
              Owner
            </div>
            <div className="mt-1 text-[0.82rem] font-medium text-[#06040c]/72">
              Site manager
            </div>
          </div>
        </div>
        <div className="mt-4 rounded-[0.9rem] border border-[#ece7dc] px-3 py-3">
          <div className="text-[0.64rem] uppercase tracking-[0.16em] text-[#06040c]/36">
            Output
          </div>
          <div className="mt-1 text-[0.82rem] font-medium text-[#06040c]/72">
            Corrective action plan and review trail
          </div>
        </div>
        <div className="mt-5 flex items-center gap-2.5">
          {["Health & Safety", "Operations", "Invite"].map((item, index) => (
            <span
              key={item}
              className={`rounded-full px-3 py-1.5 text-[0.72rem] font-medium ${
                index === 2
                  ? "border border-[#ece7dc] text-[#06040c]/58"
                  : "bg-[#f6f2e9] text-[#06040c]/70"
              }`}
            >
              {item}
            </span>
          ))}
        </div>
      </div>
    );
  }

  if (panel === "dashboard") {
    return (
      <div className="mx-auto flex h-full w-full max-w-50 flex-col rounded-[1.25rem] bg-white p-4 shadow-[0_16px_32px_rgba(6,4,12,0.06)] sm:max-w-52">
        <div className="space-y-3 text-[0.84rem] text-[#06040c]/70">
          {[
            ["Dashboard", false],
            ["Actions", true],
            ["Incidents", false],
            ["Audits", false],
            ["Analytics", false],
          ].map(([item, active]) => (
            <div
              key={item}
              className={`flex items-center gap-3 rounded-[0.8rem] px-3 py-2 ${
                active ? "bg-[#f2eef7] text-[#06040c]" : "text-[#06040c]/66"
              }`}
            >
              <span
                className={`h-3.5 w-3.5 rounded-[0.35rem] ${
                  active ? "bg-[#ff6a3d]" : "border border-[#cfc6b9]"
                }`}
              />
              <span className="font-medium">{item}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex h-full w-full max-w-62 flex-col rounded-[1.25rem] bg-white p-5 shadow-[0_16px_32px_rgba(6,4,12,0.06)] sm:max-w-64">
      <div className="text-[0.98rem] font-semibold text-[#06040c]">
        Multi-site account
      </div>
      <div className="mt-4 text-[2.3rem] font-semibold leading-none tracking-tighter text-[#06040c]">
        248
      </div>
      <div className="mt-1 text-[0.9rem] text-[#06040c]/58">
        active users across sites
      </div>
      <div className="mt-5 space-y-3 text-[0.82rem] text-[#06040c]/62">
        <div className="rounded-[0.9rem] bg-[#f7f4ed] px-3 py-3">
          Permissions grouped by role and location
        </div>
        <div className="rounded-[0.9rem] bg-[#f7f4ed] px-3 py-3">
          Auto-renewing access and audit history
        </div>
      </div>
      <div className="mt-6 flex gap-3">
        <span className="inline-flex flex-1 items-center justify-center rounded-[0.85rem] bg-[#161616] px-3 py-2.5 text-[0.8rem] font-semibold text-white">
          Manage
        </span>
        <span className="inline-flex flex-1 items-center justify-center rounded-[0.85rem] border border-[#e6dfd3] px-3 py-2.5 text-[0.8rem] font-semibold text-[#06040c]/64">
          Review
        </span>
      </div>
    </div>
  );
}

export default function PlatformExperience() {
  const shouldReduceMotion = useReducedMotion();

  return (
    <main className="min-h-screen overflow-x-clip bg-neutral-50 text-[#06040c]">
      <div className="mx-auto flex flex-col gap-24 px-4 py-4 sm:py-8 lg:py-10">
        <PlatformOverviewExperience />

        <PlatformOverviewStatsSection />
        <SlidingShowcaseCard
          invert
          image="/images/other/platform-screen-2.webp"
        >
          <div className="space-y-4">
            <SectionTitle
              light
              className="max-w-2xl text-[2.6rem] leading-[0.92] sm:text-[3.15rem] lg:text-[3.8rem]"
              text="Keep audits, incidents and actions connected."
            />
          </div>
          <p className="max-w-xl text-base leading-7 text-white/72 sm:text-[1.02rem] sm:leading-8">
            <BlurFadeText
              text="As teams complete work on the ground, SML Connect keeps actions, incident detail and overdue items visible in one shared operational view instead of scattering them across separate tools and inboxes."
              delay={0.14}
              blur={8}
              y={16}
            />
          </p>
        </SlidingShowcaseCard>

        <section id="platform-capabilities" className="scroll-mt-32">
          <PlatformCapabilityGridSection />
        </section>

        <section className="w-full bg-neutral-100 px-6 py-10 sm:px-8 sm:py-12 lg:px-10 lg:py-24 rounded-lg">
          <VariantB_FinishedProduct />
        </section>
        <VariantB_FieldWorkflows />
        <MobileWorkflowsSection />

        <FullContactForm
          embedded
          sectionId="book-demo"
          sectionClassName="scroll-mt-32"
          subject="Platform Demo Request"
          eyebrow="SML Connect"
          title="Book Your Demo"
          introDescription="Improve health and safety for your whole company with a digitised, faster, trackable and efficient system that keeps everyone connected."
          formTitle="Contact us today to book your demo"
          formDescription="Share your details and we will come back to arrange a walkthrough of SML Connect for your team."
          successTitle="Demo request received."
          successDescription="Thanks for sending your details. The team will review your request and come back to arrange your demo."
          submitLabel="Book Your Demo"
          resetLabel="Request another demo"
        />

        <PlatformFAQSection />
      </div>
    </main>
  );
}
